import type { Registry as Services } from '@ember/service';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import { PA_AUTOPILOT_SCHEDULE_OPTIONS } from 'frontend-utils/models/hotel';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getNumberOfSecondsFromDayjsObject(dayjsObject: Dayjs) {
  return (
    dayjsObject.hour() * 3600 + dayjsObject.minute() * 60 + dayjsObject.second()
  );
}

// Helper function to format time from "H:mm:ss" to "h A" (eg- 13:00:00 to 1 PM)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function formatTimeTo12HourFormat(timeString: string) {
  return dayjs(timeString, 'H:mm:ss').format('h A');
}

// Helper function to format timezone
// The autopilot schedule times are based on hotel local time and not the user local time. Therefore, we add timezoneName next to the schedule times to make this clear to the user.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function formatTimezoneName(
  hotelAndMarketService: Services['hotel-and-market'],
) {
  const { timezoneName } = hotelAndMarketService;

  if (timezoneName) {
    return `(${timezoneName.replace('_', ' ')})`;
  }

  return '';
}

export interface PushFrequency {
  id: keyof typeof PA_AUTOPILOT_SCHEDULE_OPTIONS;
  times: string[];
}

// Helper function to format the next push message based on the autopilot push frequency
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function formatAutopilotFrequencyMessage(
  pushFrequency: PushFrequency,
  hotelAndMarketService: Services['hotel-and-market'],
  intl: Services['intl'],
) {
  if (pushFrequency.id === PA_AUTOPILOT_SCHEDULE_OPTIONS.HOURLY) {
    return intl.t(tKey('pricing_assistant.autopilot.message.frequency.hourly'));
  }

  if (
    pushFrequency.id === PA_AUTOPILOT_SCHEDULE_OPTIONS.DAILY ||
    pushFrequency.id === PA_AUTOPILOT_SCHEDULE_OPTIONS.THREE_TIMES_DAILY
  ) {
    // Possible times for push
    const { times } = pushFrequency;

    let timeForNextPush = '';

    // Get current hotel time
    const currentTime = hotelAndMarketService.getLocalTime();
    const currentTimeSeconds = getNumberOfSecondsFromDayjsObject(currentTime);
    const currentTimeDate = currentTime.format('YYYY-MM-DD');

    if (pushFrequency.id === PA_AUTOPILOT_SCHEDULE_OPTIONS.DAILY && times[0]) {
      // Only one possible time for push
      timeForNextPush = times[0];
    } else {
      // Filter out all possible times which are same or after current time
      const possibleTimesSameOrAfterCurrentTime = times.filter((time) => {
        // All possible time options should be on same day as current time
        const timeDayjs = dayjs(`${currentTimeDate}T${time}`);
        const timeInSeconds = getNumberOfSecondsFromDayjsObject(timeDayjs);

        // Compare time in seconds to keep the comparison agnostic from other dayjs object properties
        return timeInSeconds >= currentTimeSeconds;
      });

      if (possibleTimesSameOrAfterCurrentTime.length !== 0) {
        // If there are multiple possible times after current time, then the next push will happen at the closest time
        timeForNextPush = possibleTimesSameOrAfterCurrentTime.reduce(
          (prev, curr) =>
            getNumberOfSecondsFromDayjsObject(
              dayjs(`${currentTimeDate}T${curr}`),
            ) <
            getNumberOfSecondsFromDayjsObject(
              dayjs(`${currentTimeDate}T${prev}`),
            )
              ? curr
              : prev,
        );
      } else if (times[0]) {
        // If all possible times are before current time, then the next push will happen next day at the first time
        timeForNextPush = times[0];
      }
    }

    let formattedTime = formatTimeTo12HourFormat(timeForNextPush);
    const formattedTimezoneName = formatTimezoneName(hotelAndMarketService);

    // Append timezone name to the time if it exists
    if (formattedTimezoneName) {
      formattedTime = `${formattedTime} ${formattedTimezoneName}`;
    }

    // timeForNextPush is a simple time string (e.g. "13:00:00"), while currentTime is a local time dayjs object
    // We compare only the hour:minutes:seconds part (converted to seconds) of timeForNextPush and currentTime
    const timeForNextPushSeconds = getNumberOfSecondsFromDayjsObject(
      dayjs(`${currentTimeDate}T${timeForNextPush}`),
    );

    const isTimeForNextPushBeforeCurrentTime =
      timeForNextPushSeconds < currentTimeSeconds;

    // If time for next push is before current time, that means the next push will happen tomorrow
    const xTimesDailyTranslationKey = isTimeForNextPushBeforeCurrentTime
      ? tKey(
          'pricing_assistant.autopilot.message.frequency.x_times_daily.tomorrow',
        )
      : tKey('pricing_assistant.autopilot.message.frequency.x_times_daily');

    return intl.t(xTimesDailyTranslationKey, {
      time: formattedTime,
      class: 'text-300--bold',
      htmlSafe: true,
    });
  }

  return '';
}

// Helper function to show autopilot push message based on if the day has recommendation or not (currently used in day detail modal and calendar tooltip)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function formatAutopilotPushMessage(
  hasRecommendation: boolean,
  pushFrequency: PushFrequency,
  hotelAndMarketService: Services['hotel-and-market'],
  intl: Services['intl'],
) {
  if (hasRecommendation) {
    return formatAutopilotFrequencyMessage(
      pushFrequency,
      hotelAndMarketService,
      intl,
    );
  }

  return intl.t(tKey('pricing_assistant.autopilot.message.no_push'));
}
